import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Sparkles } from 'lucide-react';
import AnimatedCounter from './AnimatedCounter';
import { useCustomImages } from '@/context/ImageContext';

const HeroSection = () => {
  const { getImage } = useCustomImages();
  const heroBg = getImage('homeHeroBg', '/hero-image-stellars-estates.webp');

  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) element.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToServices = () => {
    const element = document.getElementById('services');
    if (element) element.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      id="home"
      className="relative min-h-[90vh] bg-[#181F32] overflow-hidden flex items-center pt-16 pb-20 lg:pt-24 lg:pb-28"
    >
      {/* Hero Background Image filling the entire section */}
      <div className="absolute inset-0 z-0 bg-[#181F32]">
        <img
          src={heroBg}
          alt="Stellars Estates Hero Background"
          decoding="async"
          loading="eager"
          className="w-full h-full object-cover object-bottom"
        />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
        <div className="max-w-3xl text-left">
          {/* Conversion Focus Pill Badge */}
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 mb-6"
          >
            <span className="glass-badge">
              <Sparkles className="w-4 h-4 text-[#F3453F]" />
              <span>Built on Jupiter & Deployed on Earth</span>
            </span>
          </motion.div>

          {/* Headline */}
          <motion.h1
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-normal font-heading tracking-tight text-left drop-shadow-md max-w-xl"
          >
            Built Beyond <span className="font-ordinary font-normal not-italic text-[#F3453F] uppercase tracking-[0.10em] text-[1.1em] inline-block mt-1 sm:mt-2 align-baseline">Ordinary.</span>
          </motion.h1>

          {/* Subheading */}
          <motion.p
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="mt-6 text-lg sm:text-xl text-white/90 max-w-xl text-left leading-relaxed font-sans drop-shadow-sm"
          >
            We combine digital marketing, web development <br className="hidden sm:inline" />
            and brand promotion to build stronger brands <br className="hidden sm:inline" />
            and generate measurable business growth.
          </motion.p>

          {/* Primary CTA & Secondary Action */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.3 }}
            className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center justify-start gap-4"
          >
            <button
              onClick={scrollToContact}
              className="w-full sm:w-auto btn-glass btn-glass-primary px-5 py-2.5 text-sm font-medium"
            >
              Accelerate Growth Now
              <ArrowRight size={16} className="text-white ml-2 inline-block" />
            </button>
            <button
              onClick={scrollToServices}
              className="w-full sm:w-auto btn-glass btn-glass-secondary px-5 py-2.5 text-sm font-medium"
            >
              Explore Solutions
            </button>
          </motion.div>

          {/* Trust Metrics / Key Statistics */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.4 }}
            className="mt-8 pt-5 border-t border-white/20 grid grid-cols-3 gap-3 sm:gap-4 text-left w-full max-w-xl"
          >
            <div className="glass-panel px-3 py-2.5 sm:px-4 sm:py-3 flex flex-col justify-center text-left">
              <div className="text-xl sm:text-2xl font-extrabold text-[#F3453F] font-heading leading-tight">
                <AnimatedCounter value="500+" />
              </div>
              <p className="text-xs text-white/90 font-medium mt-0.5">Campaigns Executed</p>
            </div>

            <div className="glass-panel px-3 py-2.5 sm:px-4 sm:py-3 flex flex-col justify-center text-left">
              <div className="text-xl sm:text-2xl font-extrabold text-[#F3453F] font-heading leading-tight">
                <AnimatedCounter value="4.8x" />
              </div>
              <p className="text-xs text-white/90 font-medium mt-0.5">Average Client ROI</p>
            </div>

            <div className="glass-panel px-3 py-2.5 sm:px-4 sm:py-3 flex flex-col justify-center text-left">
              <div className="text-xl sm:text-2xl font-extrabold text-[#F3453F] font-heading leading-tight">
                <AnimatedCounter value="₹50M+" />
              </div>
              <p className="text-xs text-white/90 font-medium mt-0.5">Revenue Generated</p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
